import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, ExternalLink } from 'lucide-react';
import Modal from './ui/Modal';
import MentionRenderer from './effeisbuck/MentionRenderer';
import api from '../lib/api';
import { timeAgo } from '../utils/timeAgo';
import { useAuthStore } from '../stores/auth.store';
import {
  apriDestinazioneNotifica,
  notificaCambioNotifiche,
  risolviDestinazioneNotifica,
} from '../lib/notificheNavigazione';

// Auto-pops on every route change when there are unread notifications more
// recent than the last time we showed the popup. State is per-user via
// localStorage so a single browser shared by multiple accounts doesn't leak
// dismissals across users.
//
//   localStorage["wfm-notif-popup-lastshown-<userId>"] = ISO timestamp
//
// On dismiss / "Apri tutte" / "Segna tutto come letto" we set that value to
// the current time so the popup doesn't reappear until *another* new
// notification arrives.
function lastShownKey(userId) {
  return `wfm-notif-popup-lastshown-${userId}`;
}

function readLastShown(userId) {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(lastShownKey(userId));
    if (!raw) return null;
    const t = new Date(raw);
    return Number.isNaN(t.getTime()) ? null : t;
  } catch {
    return null;
  }
}

function writeLastShown(userId, when = new Date()) {
  if (!userId) return;
  try {
    localStorage.setItem(lastShownKey(userId), when.toISOString());
  } catch {
    // ignore quota / private mode
  }
}

const TYPE_BADGE = {
  info:    { label: 'Info',     bg: 'var(--wfm-info-bg)',    color: 'var(--wfm-info-text)',    border: 'var(--wfm-info-border)' },
  warning: { label: 'Avviso',   bg: 'var(--wfm-warning-bg)', color: 'var(--wfm-warning-text)', border: 'var(--wfm-warning-border)' },
  error:   { label: 'Errore',   bg: 'var(--wfm-error-bg)',   color: 'var(--wfm-error-text)',   border: 'var(--wfm-error-border)' },
  success: { label: 'Successo', bg: 'var(--wfm-success-bg)', color: 'var(--wfm-success-text)', border: 'var(--wfm-success-border)' },
};

export default function NewNotificationsPopup() {
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const location = useLocation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const lastCheckRef = useRef(0);

  const check = useCallback(async () => {
    if (!isAuthenticated || !user?.id) return;
    // De-dup checks within 1s to avoid re-firing on rapid re-renders.
    const now = Date.now();
    if (now - lastCheckRef.current < 1000) return;
    lastCheckRef.current = now;

    try {
      const res = await api.get('/notifications?page=1&limit=20');
      const list = res.data || [];
      const lastShown = readLastShown(user.id);
      // "New since last shown" = unread AND created after last popup display.
      // First-ever load (no lastShown stored) shows any unread items so the
      // user is immediately aware of pending notifications on first login.
      const fresh = list.filter((n) => {
        if (n.read) return false;
        if (!lastShown) return true;
        return new Date(n.createdAt).getTime() > lastShown.getTime();
      });
      if (fresh.length > 0) {
        setItems(fresh);
        setOpen(true);
      }
    } catch {
      // silent — bell handles its own error states
    }
  }, [isAuthenticated, user?.id]);

  // Re-check on route change AND on auth flip to authenticated.
  useEffect(() => { check(); }, [check, location.pathname]);

  const dismiss = () => {
    if (user?.id) writeLastShown(user.id);
    setOpen(false);
    setItems([]);
  };

  const onOpenAll = () => {
    if (user?.id) writeLastShown(user.id);
    setOpen(false);
    setItems([]);
    navigate('/notifiche');
  };

  const onMarkAllRead = async () => {
    try {
      await api.post('/notifications/read-all');
      notificaCambioNotifiche();
    } catch {
      // silent
    }
    dismiss();
  };

  const onItemClick = async (n) => {
    try {
      await api.patch(`/notifications/${n.id}/read`);
      notificaCambioNotifiche();
    } catch {}
    dismiss();
    apriDestinazioneNotifica(n, navigate);
  };

  if (!open || items.length === 0) return null;

  const previewCount = Math.min(items.length, 5);
  const more = items.length - previewCount;

  return (
    <Modal
      onClose={dismiss}
      size="lg"
      title={
        <span className="flex items-center gap-2">
          <Bell size={20} style={{ color: 'var(--wfm-primary)' }} />
          {items.length === 1 ? 'Hai una nuova notifica' : `Hai ${items.length} nuove notifiche`}
        </span>
      }
    >

        <ul className="space-y-1.5 max-h-[50vh] overflow-y-auto">
          {items.slice(0, previewCount).map((n) => {
            const badge = TYPE_BADGE[n.type] || TYPE_BADGE.info;
            const dest = risolviDestinazioneNotifica(n);
            return (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => onItemClick(n)}
                  className="w-full text-left p-2.5 rounded-lg transition-colors"
                  style={{ backgroundColor: 'var(--wfm-bg-light)' }}
                >
                  <div className="flex items-start gap-2">
                    <span
                      className="text-[11px] font-semibold px-1.5 py-0.5 rounded shrink-0 mt-0.5"
                      style={{ backgroundColor: badge.bg, color: badge.color, border: `1px solid ${badge.border}` }}
                    >
                      {badge.label}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="wfm-small font-bold truncate" style={{ color: 'var(--wfm-text-main)' }}>
                        {n.title}
                      </p>
                      <p className="wfm-small line-clamp-2" style={{ color: 'var(--wfm-text-mid)' }}>
                        <MentionRenderer body={n.body} interactive={false} />
                      </p>
                      <p className="wfm-tiny mt-0.5" style={{ color: 'var(--wfm-text-light)' }}>
                        {timeAgo(n.createdAt)}
                        {dest.kind !== 'fallback' && (
                          <span className="ml-2 inline-flex items-center gap-1" style={{ color: 'var(--wfm-primary-ink)' }}>
                            <ExternalLink size={10} /> Apri dettaglio
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
          {more > 0 && (
            <li className="wfm-tiny px-2.5 py-1" style={{ color: 'var(--wfm-text-light)' }}>
              ...e altre {more}
            </li>
          )}
        </ul>

        <div className="flex gap-2 justify-end mt-4">
          <button onClick={onMarkAllRead} className="wfm-btn-secondary wfm-btn-sm">
            <CheckCheck size={14} /> Segna tutto come letto
          </button>
          <button onClick={onOpenAll} className="wfm-btn-primary wfm-btn-sm">
            <ExternalLink size={14} /> Apri tutte
          </button>
        </div>
    </Modal>
  );
}
